// Harbour Rush rules. Integer maths only; backend/rush.py mirrors every function so the server can replay a run.
export const RULES = 4;
export const DURATION = 30000, MAX_TAPS = 300, SPOTS = 9;
export const FIRST = 800, REST = 250;
export const GAP_START = 760, GAP_END = 360;
export const LIFE_START = 1800, LIFE_END = 850, LIFE_VARY = 25;
export const GOLD_PCT = 12, GOLD_LIFE_PCT = 60, GOLD_PTS = 3;
export const SPEED = 10, STEP = 5, MULT_MAX = 4;
export const WAITING = 0, HIT = 1, GONE = 2;

// Park-Miller minimal standard, inclusive range.
const rand = (g, lo, hi) => {
  g.rng = (g.rng * 48271) % 2147483647;
  return lo + (g.rng % (hi - lo + 1));
};

// Every ship of a run for a seed in [0, 2^31): { spot, at, life, gold }, in order of arrival.
export function schedule(seed) {
  const g = { rng: (seed % 2147483646) + 1 }, ships = [], free = Array(SPOTS).fill(0);
  let t = FIRST;
  while (t < DURATION) {
    const p = Math.floor((t * 1000) / DURATION);
    let life = LIFE_START - Math.floor(((LIFE_START - LIFE_END) * p) / 1000);
    const gap = GAP_START - Math.floor(((GAP_START - GAP_END) * p) / 1000);
    const gold = rand(g, 0, 99) < GOLD_PCT;
    life = Math.floor((Math.floor((life * (gold ? GOLD_LIFE_PCT : 100)) / 100) * rand(g, 100 - LIFE_VARY, 100 + LIFE_VARY)) / 100);
    const open = [];
    for (let s = 0; s < SPOTS; s++) if (free[s] <= t) open.push(s);
    if (open.length) {
      const spot = open[rand(g, 0, open.length - 1)];
      ships.push({ spot, at: t, life, gold });
      free[spot] = t + life + REST;
    }
    t += gap + rand(g, 0, Math.floor(gap / 3));
  }
  return ships;
}

export function newGame(seed) {
  const ships = schedule(seed);
  return { ships, state: ships.map(() => WAITING), now: 0, score: 0, hits: 0, streak: 0, best: 0, misses: 0, ms: 0 };
}

export const mult = (g) => Math.min(MULT_MAX, 1 + Math.floor(g.streak / STEP));

// SPEED for a ship tapped the moment it turned up, down to 1 for one tapped as it leaves.
export const speed = (s, t) => 1 + Math.floor((SPEED * (s.at + s.life - 1 - t)) / s.life);

// Ships that sailed off by t break the streak.
export function advance(g, t) {
  g.ships.forEach((s, i) => {
    if (g.state[i] === WAITING && s.at + s.life <= t) {
      g.state[i] = GONE;
      g.streak = 0;
    }
  });
  g.now = Math.max(g.now, t);
}

// A tap on a spot at time t: a ship there scores its points times its speed and the multiplier, empty water resets the streak.
export function tap(g, t, spot) {
  advance(g, t);
  const i = g.ships.findIndex((s, j) => s.spot === spot && s.at <= t && g.state[j] === WAITING);
  if (i < 0) {
    g.streak = 0;
    g.misses += 1;
    return { result: "miss", points: 0, i: null };
  }
  const s = g.ships[i], points = (s.gold ? GOLD_PTS : 1) * speed(s, t) * mult(g);
  g.state[i] = HIT;
  g.streak += 1;
  g.best = Math.max(g.best, g.streak);
  g.score += points;
  g.hits += 1;
  g.ms = t;
  return { result: "hit", points, i };
}

// Replay a run.
export function simulate(seed, taps) {
  const g = newGame(seed);
  for (const [t, s] of taps) tap(g, t, s);
  return { score: g.score, hits: g.hits, best: g.best, misses: g.misses, ms: g.ms };
}
